import React, { useState, useEffect } from 'react'
import {
  Box, Paper, Typography, TextField, Button,
  CircularProgress, Alert, InputAdornment, IconButton
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import LockIcon from '@mui/icons-material/Lock'
import { useMutation, gql } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import Logo from '../assets/img/rushmyfood-logo.png'

const SET_STAFF_PASSWORD = gql`
  mutation SetStaffPassword($token: String!, $password: String!) {
    setStaffPassword(token: $token, password: $password) {
      result
      message
    }
  }
`

function VerifyStaffEmail() {
  const location = useLocation()
  const [token, setToken] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [status, setStatus] = useState('form') // form | loading | success | error
  const [message, setMessage] = useState('')
  const [validationError, setValidationError] = useState('')

  const [setStaffPassword] = useMutation(SET_STAFF_PASSWORD)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const t = params.get('token')
    if (!t) {
      setStatus('error')
      setMessage('Invalid verification link. Please ask your manager to resend the invitation.')
    } else {
      setToken(t)
    }
  }, [])

  const handleSubmit = async () => {
    setValidationError('')
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      setValidationError('Passwords do not match')
      return
    }
    setStatus('loading')
    try {
      const { data } = await setStaffPassword({ variables: { token, password } })
      if (data?.setStaffPassword?.result) {
        setStatus('success')
        setMessage(data.setStaffPassword.message)
      } else {
        setStatus('error')
        setMessage(data?.setStaffPassword?.message || 'Something went wrong.')
      }
    } catch (err) {
      setStatus('error')
      setMessage(err.message || 'Something went wrong.')
    }
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: '0.95rem',
      '&:hover fieldset': { borderColor: '#3b82f6' },
      '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
    }
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      p: 2
    }}>
      <Paper elevation={0} sx={{
        borderRadius: '20px',
        p: { xs: 3, sm: 4.5 },
        maxWidth: 420,
        width: '100%',
        border: '1px solid #e2e8f0',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
      }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src={Logo} alt="RushMyFood" style={{ height: 48, marginBottom: 12 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#0f172a' }}>
            Set Your Password
          </Typography>
          <Typography sx={{ fontSize: '0.83rem', color: '#64748b', mt: 0.5 }}>
            Create a password to activate your staff account
          </Typography>
        </Box>

        {/* Loading */}
        {status === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#3b82f6' }} />
            <Typography sx={{ mt: 2, color: '#64748b' }}>Setting your password...</Typography>
          </Box>
        )}

        {/* Form */}
        {status === 'form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="New Password"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
              size="small"
              sx={inputSx}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPass(v => !v)}>
                      {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              fullWidth
              size="small"
              sx={inputSx}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm(v => !v)}>
                      {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {validationError && (
              <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.83rem' }}>{validationError}</Alert>
            )}

            <Button
              onClick={handleSubmit}
              fullWidth
              variant="contained"
              sx={{
                mt: 1,
                py: 1.4,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' }
              }}
            >
              Activate Account
            </Button>

            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>
              Password must be at least 8 characters
            </Typography>
          </Box>
        )}

        {/* Success */}
        {status === 'success' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#16a34a', mb: 1.5 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#0f172a', mb: 1 }}>
              Account Activated!
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.87rem', mb: 3 }}>
              {message || 'Your password has been set. You can now log in.'}
            </Typography>
            <Button
              href="/#/auth/login"
              fullWidth
              variant="contained"
              sx={{
                py: 1.4,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' }
              }}
            >
              Go to Login
            </Button>
          </Box>
        )}

        {/* Error */}
        {status === 'error' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <ErrorOutlineIcon sx={{ fontSize: 64, color: '#ef4444', mb: 1.5 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#0f172a', mb: 1 }}>
              Link Invalid or Expired
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.87rem', mb: 3 }}>
              {message}
            </Typography>
            <Button
              href="/#/auth/login"
              fullWidth
              variant="outlined"
              sx={{ py: 1.4, borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            >
              Go to Login
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default VerifyStaffEmail
